import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { GenericFormComponent } from '../../../../shared/components/generic-form/generic-form.component';
import { Game } from '../../../../shared/model/game';
import { GameService } from '../../services/game.service';
import { GenericSearchSelectComponent } from '../../../../shared/components/generic-search-select/generic-search-select.component';
import { LocationFormComponent } from '../../../../shared/components/location-form/location-form.component';
import { GameTable } from '../../../../shared/model/game-table';
import { Event } from '../../../../shared/model/event';
import { Organization } from '../../../../shared/model/organization';
import { GeoLocationService } from '../../services/geo-location.service';
import { GeoLocation } from '../../../../shared/model/geoLocation';
import { startWith } from 'rxjs';
import { QuillModule } from 'ngx-quill';
import { DomSanitizer } from '@angular/platform-browser';
import { SecurityContext } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { GameCreateComponent } from '../../games/game-add/game-create/game-create.component';
import { AlertService } from '../../../../shared/services/alert.service';
import { Messages } from '../../../../shared/constants/messages';

@Component({
  selector: 'app-game-table-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    GenericFormComponent,
    GenericSearchSelectComponent,
    LocationFormComponent,
    QuillModule,
    GameCreateComponent,
  ],
  templateUrl: './game-table-form.component.html',
  styleUrl: './game-table-form.component.css',
})
export class GameTableFormComponent implements OnInit {
  private fb: FormBuilder = inject(FormBuilder);
  private gameService: GameService = inject(GameService);
  private geoLocationService: GeoLocationService = inject(GeoLocationService);
  private sanitizer: DomSanitizer = inject(DomSanitizer);
  private modalService: NgbModal = inject(NgbModal);
  private alertService: AlertService = inject(AlertService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  @Input() title: string = '';
  @Input() submitLabel: string = 'Salva';
  @Input() initialData?: GameTable;
  @Input() availableEvents: Array<Event> = [];
  @Input() availableOrganizations: Organization[] = [];

  public get filteredOrganizations(): Organization[] {
    const event = this.gdtForm?.get('associatedEvent')?.value || this.gdrForm?.get('associatedEvent')?.value || this.preselectedEvent;
    if (!event) return this.availableOrganizations;

    const eventOrgsIds = new Set<number>();
    if (event.organization?.id) eventOrgsIds.add(event.organization.id);
    if (event.invitedOrganizations) {
      event.invitedOrganizations.forEach((org: Organization) => eventOrgsIds.add(org.id));
    }

    return this.availableOrganizations.filter(org => eventOrgsIds.has(org.id));
  }

  @Input() preselectedEvent?: Event;
  @Input() defaultTableType?: 'GDR' | 'GDT';
  @Input() lockTableType: boolean = false;

  @Output() formSubmit = new EventEmitter<Partial<GameTable>>();
  @Output() cancel = new EventEmitter<void>();
  @Output() gameCreated = new EventEmitter<Game>();

  gdtForm!: FormGroup;
  gdrForm!: FormGroup;
  activeTableType: 'GDT' | 'GDR' = 'GDT';

  public selectedBoardgames = signal<Game[]>([]);
  public boardgameSearchControl = new FormControl<Game | null>(null);

  protected availableGames = signal<Array<Game>>([]);
  protected selectedGame?: Game;
  protected readonly quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
  };

  public get displayTitle(): string {
    const tType = this.activeTableType;
    if (tType === 'GDR' && !this.title.includes('GDR')) {
      return `${this.title} (GDR)`;
    }
    if (tType === 'GDT' && !this.title.includes('GDT')) {
      return `${this.title} (GDT)`;
    }
    return this.title;
  }

  constructor() {
    const locationGroup = this.fb.group({
      id: [''],
      formalName: [''],
      address: [''],
      city: [''],
      country: [''],
      town: [''],
      village: [''],
      neighbourhood: [''],
      residential: [''],
      postcode: [''],
      street: [''],
      houseNumber: [''],
      province: [''],
      latitude: [undefined],
      longitude: [undefined],
      rawInput: [''],
      displayName: [''],
      lat: [''],
      lon: [''],
    });
    locationGroup.addValidators(this.locationRequiredWhenOfflineValidator.bind(this));

    this.gdtForm = this.fb.group({
      tableName: ['', [Validators.maxLength(100)]],
      maxPlayers: [4, [Validators.required, Validators.min(1)]],
      playedGame: [null, Validators.required],
      associatedEvent: [null],
      organizationId: [null as number | null, Validators.required],
    });

    this.gdrForm = this.fb.group({
      gameMasterName: ['', Validators.required],
      tableName: ['', Validators.required],
      description: ['', [Validators.required, this.richTextRequiredValidator.bind(this)]],
      minPlayers: [1, [Validators.required, Validators.min(1)]],
      maxPlayers: [1, [Validators.required, Validators.min(1)]],
      minAge: [null as number | null, [Validators.min(0)]],
      isOnline: [false, Validators.required],
      active: [true, Validators.required],
      autoAccept: [true, Validators.required],
      playedGame: [null, Validators.required],
      associatedEvent: [null],
      organizationId: [null as number | null, Validators.required],
      location: locationGroup,
    });

    this.displayGame = this.displayGame.bind(this);
    this.displayEvent = this.displayEvent.bind(this);
  }

  get activeForm(): FormGroup {
    return this.activeTableType === 'GDT' ? this.gdtForm : this.gdrForm;
  }

  setTableType(type: 'GDT' | 'GDR'): void {
    if (this.activeTableType === type) return;
    const currentValues = this.activeForm.getRawValue();
    this.activeTableType = type;
    const targetForm = this.activeForm;
    targetForm.patchValue({
      tableName: currentValues.tableName,
      playedGame: currentValues.playedGame,
      associatedEvent: currentValues.associatedEvent,
      organizationId: currentValues.organizationId,
      maxPlayers: currentValues.maxPlayers || (type === 'GDT' ? 4 : 1),
    });
    if (currentValues.playedGame) {
      this.selectedGame = currentValues.playedGame;
    }
  }

  ngOnInit(): void {
    this.loadAvailableGames();

    const initialTableType = this.initialData?.tableType || this.defaultTableType || 'GDT';
    this.activeTableType = initialTableType;

    if (this.initialData) {
      this.selectedGame = this.initialData.playedGame;
      const normalizedLocation = this.geoLocationService.normalizeLocation(
        this.initialData.location as unknown,
      ) as GeoLocation;
      if (!normalizedLocation?.formalName && normalizedLocation?.displayName) {
        normalizedLocation.formalName = normalizedLocation.displayName;
      }

      if (this.activeTableType === 'GDT') {
        this.gdtForm.patchValue({
          tableName: this.initialData.tableName,
          maxPlayers: this.initialData.maxPlayers,
          playedGame: this.initialData.playedGame,
          associatedEvent: this.initialData.event ?? null,
          organizationId:
            this.initialData.organization?.id ?? this.initialData.event?.organization?.id ?? null,
        });

        if (this.initialData.boardgames && this.initialData.boardgames.length > 0) {
          this.selectedBoardgames.set([...this.initialData.boardgames]);
        } else if (this.initialData.playedGame) {
          this.selectedBoardgames.set([this.initialData.playedGame]);
        }
      } else {
        this.gdrForm.patchValue({
          gameMasterName: this.initialData.gameMasterName || '',
          tableName: this.initialData.tableName,
          description: this.initialData.description,
          minPlayers: this.initialData.minPlayers,
          maxPlayers: this.initialData.maxPlayers,
          minAge: this.initialData.minAge ?? null,
          isOnline: this.initialData.isOnline,
          active: this.initialData.isActive,
          autoAccept: this.initialData.isAutoAccept,
          playedGame: this.initialData.playedGame,
          associatedEvent: this.initialData.event ?? null,
          organizationId:
            this.initialData.organization?.id ?? this.initialData.event?.organization?.id ?? null,
          location: normalizedLocation,
        });
      }
      this.playedGameControl.setValue(this.initialData.playedGame);
    }

    this.bindLocationValidationToOnlineFlag();
    this.bindLocationAutoFillFromEvent();
    this.bindOrganizationFromEvent();

    if (this.preselectedEvent) {
      this.gdtForm.get('associatedEvent')?.setValue(this.preselectedEvent);
      this.gdrForm.get('associatedEvent')?.setValue(this.preselectedEvent);
      this.applyOrganizationFromEvent(this.gdtForm, this.preselectedEvent);
      this.applyOrganizationFromEvent(this.gdrForm, this.preselectedEvent);
      this.applyLocationFromEvent(this.preselectedEvent);
    } else if (this.initialData?.event) {
      const fullEvent = this.availableEvents.find((e) => e.id === this.initialData?.event?.id) ?? this.initialData.event;
      this.gdtForm.get('associatedEvent')?.setValue(fullEvent);
      this.gdrForm.get('associatedEvent')?.setValue(fullEvent);
      this.applyOrganizationFromEvent(this.gdtForm, fullEvent);
      this.applyOrganizationFromEvent(this.gdrForm, fullEvent);
      this.applyLocationFromEvent(fullEvent);
    }

    this.cdr.detectChanges();
  }

  public displayGame(game: Game): string {
    if (game && game.gameName) {
      return game.gameName;
    }
    return '';
  }

  public displayEvent(event: Event): string {
    return event?.name || '';
  }

  public get playedGameControl(): FormControl<Game | null> {
    return this.activeForm.get('playedGame') as FormControl<Game | null>;
  }

  addBoardgame(game: Game) {
    if (game && !this.selectedBoardgames().some(g => g.id === game.id)) {
      this.selectedBoardgames.update(games => [...games, game]);

      if (this.selectedBoardgames().length === 1) {
        this.gdtForm.patchValue({ playedGame: game });
        this.gdtForm.get('playedGame')?.updateValueAndValidity();
      }
    }
    this.boardgameSearchControl.setValue(null, { emitEvent: false });
  }

  removeBoardgame(game: Game) {
    this.selectedBoardgames.update(games => games.filter(g => g.id !== game.id));
    if (this.selectedBoardgames().length > 0) {
      this.gdtForm.patchValue({ playedGame: this.selectedBoardgames()[0] });
    } else {
      this.gdtForm.patchValue({ playedGame: null });
    }
    this.gdtForm.get('playedGame')?.updateValueAndValidity();
  }

  public get associatedEventControl(): FormControl<Event | null> {
    return this.activeForm.get('associatedEvent') as FormControl<Event | null>;
  }

  public get locationGroup(): FormGroup {
    return this.gdrForm.get('location') as FormGroup;
  }

  onSubmit() {
    if (this.activeForm.valid) {
      const formValue = this.activeForm.getRawValue();
      const selectedOrganization = this.filteredOrganizations.find(
        (org) => org.id === Number(formValue.organizationId),
      ) ?? this.availableOrganizations.find(
        (org) => org.id === Number(formValue.organizationId),
      );

      let tableData: Partial<GameTable>;

      if (this.activeTableType === 'GDT') {
        const orgName = selectedOrganization?.organizationName ?? this.initialData?.organization?.organizationName ?? this.preselectedEvent?.organization?.organizationName ?? '';
        const fallbackTableName = orgName ? `Presidio ${orgName}` : 'Presidio GdT';
        const finalTableName = formValue.tableName && formValue.tableName.trim() !== '' ? formValue.tableName : fallbackTableName;

        const boardgames = this.selectedBoardgames();
        const primaryGame = boardgames.length > 0 ? boardgames[0] : formValue.playedGame;

        tableData = {
          tableType: 'GDT',
          tableName: finalTableName,
          description: 'Tavolo GdT',
          minPlayers: 1,
          maxPlayers: Number(formValue.maxPlayers),
          minAge: primaryGame?.minAge ?? undefined,
          isOnline: false,
          isActive: true,
          isAutoAccept: true,
          playedGame: primaryGame,
          boardgames: boardgames,
          organization: selectedOrganization
            ? { id: selectedOrganization.id, organizationName: selectedOrganization.organizationName }
            : (this.initialData?.organization ?? this.preselectedEvent?.organization),
          location: (this.preselectedEvent || formValue.associatedEvent)?.location,
        };
      } else {
        const sanitizedDescription = this.sanitizedHtml(formValue.description);
        tableData = {
          tableType: 'GDR',
          gameMasterName: formValue.gameMasterName,
          tableName: formValue.tableName,
          description: sanitizedDescription,
          minPlayers: formValue.minPlayers,
          maxPlayers: formValue.maxPlayers,
          minAge: formValue.minAge != null && formValue.minAge !== '' ? Number(formValue.minAge) : undefined,
          isOnline: formValue.isOnline,
          isActive: formValue.active,
          isAutoAccept: formValue.autoAccept,
          playedGame: formValue.playedGame,
          organization: selectedOrganization
            ? { id: selectedOrganization.id, organizationName: selectedOrganization.organizationName }
            : (this.initialData?.organization ?? this.preselectedEvent?.organization),
        };

        if (!formValue.isOnline) {
          tableData.location = this.geoLocationService.toApiLocation(formValue.location) ?? undefined;
        }
      }

      if (formValue.associatedEvent?.id) {
        tableData.event = { id: Number(formValue.associatedEvent.id) } as Event;
      }

      this.formSubmit.emit(tableData);
      return;
    }

    this.activeForm.markAllAsTouched();
  }

  onCancel() {
    this.cancel.emit();
  }

  openCreateGameModal(content: unknown): void {
    this.modalService.open(content, { size: 'lg', backdrop: 'static' });
  }

  onGameCreated(game: Game, modal: NgbModalRef): void {
    this.gameService.submitGame(game).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const newGame = response.data;
          this.availableGames.update((games) => [newGame, ...games]);

          if (this.activeTableType === 'GDT') {
            this.addBoardgame(newGame);
          } else {
            this.playedGameControl.setValue(newGame);
            this.gameSelected(newGame);
          }

          this.gameCreated.emit(newGame);
          this.alertService.showSuccess(Messages.GAME_CREATED_SUCCESS);
          modal.close();
        }
      },
      error: (err) => {
        if (!err?.error?.message) {
          this.alertService.showError(Messages.UNEXPECTED_ERROR);
        }
      },
    });
  }

  gameSelected(game: Game) {
    if (game) {
      this.selectedGame = game;
      const minP = Math.max(1, Number(game.minPlayers) || 1);
      const maxP = Math.max(minP, Number(game.maxPlayers) || 4);
      this.gdtForm.patchValue({
        playedGame: game,
        maxPlayers: maxP,
      });
      this.gdrForm.patchValue({
        playedGame: game,
        minPlayers: minP,
        maxPlayers: maxP,
        minAge: game.minAge ?? null,
      });
      this.activeForm.updateValueAndValidity();
      this.cdr.markForCheck();
    }
  }

  private loadAvailableGames(): void {
    this.gameService.getAllGames().subscribe({
      next: (response) => {
        const loadedGames = response.success && response.data ? response.data : [];

        if (
          this.initialData?.playedGame &&
          !loadedGames.some((game) => game.id === this.initialData?.playedGame?.id)
        ) {
          this.availableGames.set([...loadedGames, this.initialData.playedGame]);
          return;
        }

        this.availableGames.set(loadedGames);
      },
      error: () => {
        this.availableGames.set(this.initialData?.playedGame ? [this.initialData.playedGame] : []);
      },
    });
  }

  private bindLocationValidationToOnlineFlag(): void {
    this.gdrForm
      .get('isOnline')
      ?.valueChanges.pipe(startWith(this.gdrForm.get('isOnline')?.value))
      .subscribe(() => {
        this.locationGroup.updateValueAndValidity({ emitEvent: false });
      });
  }

  private applyLocationFromEvent(event: Event | null): void {
    if (!event?.location) {
      return;
    }

    const currentLoc = this.locationGroup.getRawValue();
    const hasCustomLocation = currentLoc?.formalName || currentLoc?.address || currentLoc?.city || currentLoc?.id;

    if (!hasCustomLocation) {
      const normalizedLocation = this.geoLocationService.normalizeLocation(
        event.location,
      ) as GeoLocation;
      if (!normalizedLocation.formalName && normalizedLocation.displayName) {
        normalizedLocation.formalName = normalizedLocation.displayName;
      }
      this.locationGroup.patchValue(normalizedLocation);
      this.locationGroup.markAsDirty();
      this.locationGroup.updateValueAndValidity();
      
      this.gdrForm.get('isOnline')?.setValue(false, { emitEvent: false });
      this.gdrForm.updateValueAndValidity();
    }
  }

  private bindLocationAutoFillFromEvent(): void {
    this.gdtForm.get('associatedEvent')?.valueChanges.subscribe((event) => this.applyLocationFromEvent(event));
    this.gdrForm.get('associatedEvent')?.valueChanges.subscribe((event) => this.applyLocationFromEvent(event));
  }

  private bindOrganizationFromEvent(): void {
    this.gdtForm.get('associatedEvent')?.valueChanges.subscribe((event) => {
      this.applyOrganizationFromEvent(this.gdtForm, event);
    });
    this.gdrForm.get('associatedEvent')?.valueChanges.subscribe((event) => {
      this.applyOrganizationFromEvent(this.gdrForm, event);
    });
  }

  private applyOrganizationFromEvent(form: FormGroup, event?: Event | null): void {
    if (!event?.organization?.id) {
      form.get('organizationId')?.enable({ emitEvent: false });
      form.updateValueAndValidity();
      return;
    }

    const validOrgs = this.filteredOrganizations;

    if (this.initialData?.organization?.id) {
      form.get('organizationId')?.setValue(this.initialData.organization.id, { emitEvent: false });
    } else if (!form.get('organizationId')?.value || !validOrgs.some(o => o.id === form.get('organizationId')?.value)) {
      // Prioritize the event organization if it's available for the user, otherwise pick the first valid option or fallback
      const defaultId = validOrgs.some(org => org.id === event.organization!.id) ? event.organization.id : (validOrgs.length > 0 ? validOrgs[0].id : event.organization.id);
      form.get('organizationId')?.setValue(defaultId, { emitEvent: false });
    }

    if (validOrgs.length > 1) {
      form.get('organizationId')?.enable({ emitEvent: false });
    } else {
      form.get('organizationId')?.disable({ emitEvent: false });
    }

    form.updateValueAndValidity();
  }

  private locationRequiredWhenOfflineValidator(control: AbstractControl): ValidationErrors | null {
    const isOnline = this.gdrForm?.get('isOnline')?.value;
    if (isOnline) {
      return null;
    }

    const associatedEvent = this.gdrForm?.get('associatedEvent')?.value || this.preselectedEvent;
    if (associatedEvent?.location) {
      return null;
    }

    if (control.get('id')?.value) {
      return null;
    }

    const location = this.geoLocationService.normalizeLocation(control.value) as
      | GeoLocation
      | undefined;
    const hasLocation = !!this.geoLocationService.locationQuery(location).trim();
    return hasLocation ? null : { locationRequired: true };
  }

  private richTextRequiredValidator(control: AbstractControl): ValidationErrors | null {
    const sanitized = this.sanitizedHtml(control.value);
    const plainText = sanitized
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
    return plainText ? null : { required: true };
  }

  private sanitizedHtml(value: unknown): string {
    return this.sanitizer.sanitize(SecurityContext.HTML, String(value ?? ''))?.trim() ?? '';
  }
}
